import { Editor } from '@tiptap/react'
import React, { useEffect, useState } from 'react'
import { TiptapEditorUpdatePayload } from '..'
import { useExtendedMenu } from '../hooks/useExtendedMenu'
import { ToolbarButtonUi } from '../ui/toolbar-button-ui'
import { ToolbarEditorUi } from '../ui/toolbar-ui'
import { ColorPickerControls } from './color-picker/color-picker-controls'
import { HeadlineControls } from './headline-controls'
import { InlineStyleControls } from './inline-style-controls'
import { LinkToolbar } from './link-toolbar/link-toolbar'

interface ToolbarProps {
  editor: Editor
  enableHeadings: boolean
  update: (payload: Partial<TiptapEditorUpdatePayload>) => void
  isBlogPage: boolean
}

export const Toolbar = ({
  update,
  editor,
  enableHeadings,
  isBlogPage,
}: ToolbarProps) => {
  const [showLinkToolbar, setShowLinkToolbar] = useState(
    !!editor.getAttributes('link').href,
  )
  const { ref, placement } = useExtendedMenu(showLinkToolbar || enableHeadings)
  const createOrEditLink = (event: React.SyntheticEvent) => {
    event.preventDefault()
    setShowLinkToolbar(true)
  }

  useEffect(() => {
    const changeShowLinkToolbar = (event: MouseEvent) => {
      const { state } = editor
      const { from, to } = state.selection

      const isTextSelected = from !== to

      const { href, popupid } = editor.getAttributes('link')
      if (!isTextSelected && (href || popupid)) {
        const target = event.target as Element
        const selectedLink = target.closest('a')

        if (selectedLink) {
          const range = document.createRange()
          const selection = window.getSelection()

          selection?.removeAllRanges()

          range.selectNodeContents(selectedLink)
          selection?.addRange(range)
        }

        setShowLinkToolbar(true)
      } else {
        setShowLinkToolbar(false)
      }
    }

    editor.view.dom.addEventListener('click', changeShowLinkToolbar)
    return () => {
      editor.view.dom.removeEventListener('click', changeShowLinkToolbar)
    }
  }, [])

  return (
    <ToolbarEditorUi ref={ref} placement={placement}>
      {showLinkToolbar ? (
        <LinkToolbar
          editor={editor}
          close={() => {
            setShowLinkToolbar(false)
          }}
          isBlogPage={isBlogPage}
        />
      ) : (
        <>
          {enableHeadings && (
            <HeadlineControls update={update} editor={editor} />
          )}
          <InlineStyleControls editor={editor} />
          <ColorPickerControls editor={editor} />
          <ToolbarButtonUi onClick={createOrEditLink}>
            <i className="fas fa-link" />
          </ToolbarButtonUi>
        </>
      )}
    </ToolbarEditorUi>
  )
}
